import styled from "@emotion/styled";
import React from "react";
import LoadingEllipse from "../../components/ButterComponents/LoadingEllipse/LoadingEllipse";
import TradeDetailsSection from "../../components/TradeDetailsSection/TradeDetailsSection";
import { Text } from "../../components/general/Text";
import {
  getErrorFromOrderError,
  getQuoteWarnings,
  getTradeErrorFromQuote,
  TRADE_SIZE_ERROR_CODE,
} from "../../config/errorCodes";
import { FlexContainer } from "../../containers/FlexContainer";
import { useTimedFetchBalances } from "../../hooks/useTimedFetchBalances";
import { useTimedQuoteRefetch } from "../../hooks/useTimedQuoteRefetch";
import { GlobalStore } from "../../store/global/GlobalStore";
import { Amount } from "../../types/amount";
import { InitialActionButton } from "./InitialActionButton";
import "./OrderEntry.css";
import { OrderEntryHeader } from "./OrderEntryHeader";
import { MakerSection } from "./sections/MakerSection";
import { TakerSection } from "./sections/TakerSection";
import { WarningOrError } from "../../components/WarningOrError/WarningOrError";
import { DisabledOverlay, ORDER_ENTRY_ZINDEXES } from "./elements/DisabledOverlay";
import { OrderStatus } from "../../types/orderStatus";
import { ReactComponent as ViewBlock } from "../../img/butter_svgs/ViewBlock.svg";
import { withIsVisible } from "../../utils/reactUtils";
import { openTransactionInBlockExplorer } from "../../utils/txUtils";
import { OrderErrorButtonsSection } from "./sections/OrderErrorButtonsSection";

export const OrderEntry: React.FC = () => {
  const {
    isRequestingQuote,
    quoteError,
    quote,
    chain,
    isChainSwitching,
    orderError,
    order,
    orderStatus,
  } = GlobalStore.useState(s => {
    return {
      isRequestingQuote: s.isRequestingQuote,
      quoteError: s.quoteError,
      quote: s.quote,
      chain: s.network,
      isChainSwitching: s.isChainSwitching,
      orderError: s.orderError,
      order: s.order,
      orderStatus: s.orderStatus,
    };
  });

  //Re-fetches quote at the specified interval
  useTimedQuoteRefetch();
  useTimedFetchBalances();
  const { networkFee } = GlobalStore.useState(s => {
    return { networkFee: s.quote?.usdGasFee };
  });
  const errorMessageFromQuoteError = getTradeErrorFromQuote(chain, quoteError);
  const errorMessageFromOrderError = getErrorFromOrderError(orderError);
  const warning = getQuoteWarnings(quote, chain);
  const showDisabledOverlay =
    isChainSwitching ||
    orderStatus === OrderStatus.IN_PROGRESS ||
    orderStatus === OrderStatus.CONFIRMING;
  const increaseBottomSectionZIndex =
    (!isChainSwitching && orderStatus === OrderStatus.IN_PROGRESS) ||
    orderStatus === OrderStatus.CONFIRMING;

  return (
    <>
      {showDisabledOverlay && (
        <DisabledOverlay
          isProcessingTrade={
            orderStatus === OrderStatus.IN_PROGRESS || orderStatus === OrderStatus.CONFIRMING
          }
        />
      )}
      <TopSectionContainer>
        <OrderEntryHeader />
        <TakerAndMakerContainer>
          <TakerSection />
          <MakerSection />
        </TakerAndMakerContainer>
        {errorMessageFromOrderError && <WarningOrError {...errorMessageFromOrderError} />}
        {errorMessageFromQuoteError &&
          quoteError?.error.errorCode !== TRADE_SIZE_ERROR_CODE &&
          !errorMessageFromOrderError && <WarningOrError {...errorMessageFromQuoteError} />}
        {warning && !errorMessageFromOrderError && <WarningOrError {...warning} />}
      </TopSectionContainer>
      <TradeBottomSectionContainer increaseBottomSectionZIndex={increaseBottomSectionZIndex}>
        <InitialActionButton isVisible={!orderError} />
        {orderError !== undefined && <OrderErrorButtonsSection />}
        <ViewTransactionContainer
          isVisible={
            orderStatus === OrderStatus.IN_PROGRESS &&
            !errorMessageFromOrderError &&
            quote?.routeType !== "Jam"
          }
          isProcessingTrade={order?.txHash === undefined}
          onClick={() => {
            if (order?.txHash !== undefined) {
              openTransactionInBlockExplorer(chain.id, order.txHash);
            }
          }}
        >
          <ViewBlock />{" "}
          <Text textStyle="Paragraph" size="Large">
            View in explorer
          </Text>
        </ViewTransactionContainer>
        {isRequestingQuote && quote === undefined && (
          <FlexContainer justifyContent="center" stretch={true}>
            <LoadingEllipse />{" "}
            <Text textStyle="Paragraph" size="Large">
              Fetching best price
            </Text>
          </FlexContainer>
        )}
        <TradeDetailsSection
          networkFee={networkFee ?? Amount.ZERO}
          isVisible={
            quote !== undefined &&
            orderError === undefined &&
            orderStatus !== OrderStatus.IN_PROGRESS
          }
        />
      </TradeBottomSectionContainer>
    </>
  );
};

// Sub-components
const TopSectionContainer = styled(FlexContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  align-self: stretch;
`;
const TakerAndMakerContainer = styled(FlexContainer)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  align-self: stretch;
`;
const TradeBottomSectionContainer = styled.div<{ increaseBottomSectionZIndex: boolean }>`
  display: flex;
  padding: 0 26px;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  z-index: ${props =>
    props.increaseBottomSectionZIndex ? ORDER_ENTRY_ZINDEXES.VIEW_TRANSACTION : "auto"};
`;

const ViewTransactionContainer = withIsVisible(styled.div<{ isProcessingTrade: boolean }>`
  display: flex;
  height: 26px;
  justify-content: center;
  align-items: flex-end;
  gap: 3px;
  color: ${props =>
    props.isProcessingTrade
      ? props.theme.button.link.text.disabled
      : props.theme.button.link.text.default};
`);
